import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { App as AntApp, Form, Input, Button, Row, Col, Typography, Space } from 'antd';

import { Encrypt } from '../utils/encryption';
import { userManagementApi } from '../config/api'; // adjust path if needed
import Zibuzz from '../assets/Ziniz-Icon-EPS.svg';
import authStyles from '../styles/authShared.module.css';
import axios from 'axios';
import {
  PrefixFormItem,
  mobileForApi,
  isMobileLengthValid,
  MOBILE_MAX_LEN,
  mobileFieldInputPlain,
  userNameDisplayFromPrefix,
} from '../constants/CountryCodes';

const { Title } = Typography;

const Register = () => {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = Form.useWatch('email', form);
  const mobileWatch = Form.useWatch('mobile', form);

  const [finalLoading, setFinalLoading] = useState(false);
  const [mobileLoading, setMobileLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [mobileResendLoading, setMobileResendLoading] = useState(false);
  const [emailResendLoading, setEmailResendLoading] = useState(false);

  const [mobileResend, setMobileResend] = useState(0);
  const [emailResend, setEmailResend] = useState(0);

  const [showMobileCodeInput, setShowMobileCodeInput] = useState(false);
  const [showEmailCodeInput, setShowEmailCodeInput] = useState(false);
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const [mobileOtp, setMobileOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');

  const [mobileToken, setMobileToken] = useState(null);
  const [emailToken, setEmailToken] = useState(null);

  const [loginHovered, setLoginHovered] = useState(false);

  const mobileInputRef = useRef(null);
  const emailInputRef = useRef(null);

  const isValidEmail = (val) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(val || '');
  };

  useEffect(() => {
    if (mobileResend > 0) {
      const t = setTimeout(() => setMobileResend((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [mobileResend]);

  useEffect(() => {
    if (emailResend > 0) {
      const t = setTimeout(() => setEmailResend((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [emailResend]);

  useEffect(() => {
    setMobileOtp('');
    setShowMobileCodeInput(false);
    setMobileToken(null);
    setIsMobileVerified(false);
    form.setFieldsValue({ userName: '' });
  }, [mobileWatch, form]);

  useEffect(() => {
    setEmailOtp('');
    setShowEmailCodeInput(false);
    setEmailToken(null);
    setIsEmailVerified(false);
  }, [email]);

  const handleMobileVerify = async (action = '') => {
    const prefix = form.getFieldValue('prefix');
    const mobileVal = form.getFieldValue('mobile');

    if (isMobileVerified && action !== 'Resend') {
      setIsMobileVerified(false);
      setShowMobileCodeInput(false);
      setMobileToken(null);
      setMobileOtp('');
      form.setFieldsValue({ userName: '' });
      return;
    }
    if (action === 'Resend') {
      setMobileOtp('');
    }

    if (!isMobileLengthValid(mobileVal)) return;

    const isResend = action === 'Resend';
    if (isResend) setMobileResendLoading(true);
    else setMobileLoading(true);
    try {
      const response = await axios.post(
        userManagementApi,
        {...Encrypt({
          action: 'verifyMobile',
          mobile: mobileForApi(prefix, mobileVal),
        }), operationType: 'register'},
        { headers: { 'Content-Type': 'application/json' } }
      );
      const token = response.data?.token;
      if (token) {
        setShowMobileCodeInput(true);
        setMobileToken(token);
        setIsMobileVerified(true);
        form.setFieldsValue({
          userName: userNameDisplayFromPrefix(prefix, mobileVal),
        });
        setMobileResend(46);
        message.success('Verification code sent to your WhatsApp.');
      }
    } catch (error) {
      const errText =
        error?.response?.data?.message ||
        'Unexpected error occurred. Please try again later.';
      form.setFields([{ name: 'mobile', errors: [errText] }]);
      message.error(errText);
      console.error('verifyMobile failed:', error);
    } finally {
      if (isResend) setMobileResendLoading(false);
      else setMobileLoading(false);
    }
  };

  const handleEmailVerify = async (action = '') => {
    const emailVal = (form.getFieldValue('email') || '').trim();

    if (isEmailVerified && action !== 'Resend') {
      setIsEmailVerified(false);
      setShowEmailCodeInput(false);
      setEmailToken(null);
      setEmailOtp('');
      return;
    }
    if (action === 'Resend') {
      setEmailOtp('');
    }

    if (!emailVal || !isValidEmail(emailVal)) return;

    const isResend = action === 'Resend';
    if (isResend) setEmailResendLoading(true);
    else setEmailLoading(true);
    try {
      const response = await axios.post(
        userManagementApi,
        {...Encrypt({
          action: 'verifyEmail',
          email: emailVal,
        }), operationType: 'register'},
        { headers: { 'Content-Type': 'application/json' } }
      );
      const token = response.data?.token;
      if (token) {
        setShowEmailCodeInput(true);
        setEmailToken(token);
        setIsEmailVerified(true);
        setEmailResend(46);
        message.success('Verification code sent to your email.');
      }
    } catch (error) {
      const errText =
        error?.response?.data?.message ||
        'Unexpected error occurred. Please try again later.';
      form.setFields([{ name: 'email', errors: [errText] }]);
      message.error(errText);
      console.error('verifyEmail failed:', error);
    } finally {
      if (isResend) setEmailResendLoading(false);
      else setEmailLoading(false);
    }
  };

  const onFinish = async (values) => {
    setFinalLoading(true);

    const emailStr = (values.email || '').trim();
    const payload = {
      action: 'user-registration',
      token: mobileToken,
      emailToken: emailToken,
      mobile: mobileForApi(values.prefix, values.mobile),
      userName: mobileForApi(values.prefix, values.mobile),
      mobileVerificationCode: mobileOtp,
      emailVerificationCode: emailStr ? emailOtp : null,
      firstName: values.firstName,
      lastName: values.lastName,
      gender: '',
      email: emailStr,
      password: values.password,
      confirmPassword: values.confirmPassword,
      dateOfBirth: '',
    };

    try {
      const response = await axios.post(userManagementApi, {...Encrypt(payload), operationType: 'register'}, {
        headers: { 'Content-Type': 'application/json' },
      });
      const responseText =
        typeof response.data?.message === 'string'
          ? response.data.message
          : '';
      if (
        response.status === 200 &&
        response.data?.message?.toLowerCase().includes('success')
      ) {
        message.success(responseText || 'Registration successful.');
        // Check if there's a redirect parameter to pass along
        const redirectTo = searchParams.get('redirect');
        const loginPath = redirectTo ? `/login?redirect=${redirectTo}` : '/login';
        setTimeout(() => navigate(loginPath), 1600);
      } else {
        message.error(
          responseText || 'Registration failed. Please check your details.'
        );
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errText =
        error.response?.data?.message ||
        'Something is not right. Please try again.';
      message.error(String(errText));
    } finally {
      setFinalLoading(false);
    }
  };

  const isFormValid = () => {
    const v = form.getFieldsValue();
    const emailStr = (v.email || '').trim();
    const mobileOk =
      v.userName &&
      mobileToken &&
      mobileOtp.length > 0 &&
      isMobileLengthValid(v.mobile);
    const emailOk =
      !emailStr ||
      (emailToken && emailOtp.length > 0 && isValidEmail(emailStr));
    return (
      mobileOk &&
      emailOk &&
      v.firstName &&
      v.lastName &&
      v.password &&
      v.confirmPassword
    );
  };

  return (
    <div className={authStyles.authPageShell}>
    <div className={authStyles.registerContainer}>
      <div className={authStyles.regLogoContainer}>
        <img src={Zibuzz} alt="Rotating ZiBuzz" className={authStyles.regLogoImg} />
      </div>
      <Title level={2}>Register as an Admin at ZiBuzz</Title>

      <div className={authStyles.registerFormPanel}>
      <Form
        form={form}
        name="register"
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        className={authStyles.registerForm}
        initialValues={{ prefix: '+91' }}
      >
        <Form.Item label="Mobile (WhatsApp only)" required>
          <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
            <Link
              to="/login"
              onMouseEnter={() => setLoginHovered(true)}
              onMouseLeave={() => setLoginHovered(false)}
              style={{
                position: 'absolute',
                top: '-1.5rem',
                right: 0,
                fontSize: '0.9rem',
                transition: 'color 0.2s ease',
                zIndex: 10,
              }}
            >
              {loginHovered ? 'Login to ZiBuzz' : 'Existing User?'}
            </Link>
          </div>
          <div className={authStyles.verifyRow}>
            <div className={authStyles.verifyRowFields}>
              <Space.Compact style={{ width: '100%' }}>
                <PrefixFormItem disabled={isMobileVerified} />
                <Form.Item
                  name="mobile"
                  noStyle
                  rules={[
                    { required: true, message: 'Please enter your mobile number' },
                    {
                      pattern: /^\d+$/,
                      message: 'Digits only',
                    },
                    {
                      validator: (_, value) => {
                        if (!value || !isMobileLengthValid(value)) {
                          return Promise.reject(
                            new Error('Enter 6–14 digits')
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <Input
                    ref={mobileInputRef}
                    className={authStyles.emailInput}
                    disabled={isMobileVerified}
                    maxLength={MOBILE_MAX_LEN}
                    style={mobileFieldInputPlain}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, MOBILE_MAX_LEN);
                      form.setFieldsValue({ mobile: v });
                    }}
                  />
                </Form.Item>
              </Space.Compact>
            </div>
            <Button
              type="primary"
              className={authStyles.verifyRowVerifyBtn}
              onClick={() => handleMobileVerify('Edit')}
              disabled={
                mobileLoading ||
                mobileResendLoading ||
                !isMobileLengthValid(mobileWatch)
              }
              loading={mobileLoading}
            >
              {mobileLoading
                ? 'Verifying...'
                : isMobileVerified
                  ? 'Edit'
                  : 'Verify'}
            </Button>
          </div>

          {showMobileCodeInput && (
            <div className={authStyles.inlineVerificationBlock}>
              <div className={authStyles.inlineVerificationOtp}>
                <span className={authStyles.inlineVerificationLabel}>WhatsApp code</span>
                <Input.OTP
                  length={6}
                  value={mobileOtp}
                  onChange={setMobileOtp}
                  disabled={mobileResendLoading}
                />
              </div>
              <Button
                type="default"
                size="small"
                className={authStyles.inlineVerificationResend}
                onClick={() => handleMobileVerify('Resend')}
                disabled={mobileResend > 0 || mobileResendLoading}
                loading={mobileResendLoading}
              >
                {mobileResend > 0 ? `Resend in ${mobileResend}s` : 'Resend code'}
              </Button>
            </div>
          )}
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[
                { required: true, message: 'Please enter your first name' },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[
                { required: true, message: 'Please enter your last name' },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Email">
          <div>
            <div className={authStyles.verifyRow}>
              <div className={authStyles.verifyRowFields}>
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item
                    name="email"
                    noStyle
                    rules={[
                      {
                        validator: (_, value) => {
                          const s = (value || '').trim();
                          if (!s) return Promise.resolve();
                          if (!isValidEmail(s)) {
                            return Promise.reject(new Error('Please enter a valid email'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Input
                      type="email"
                      ref={emailInputRef}
                      disabled={isEmailVerified}
                      className={authStyles.emailInput}
                      style={mobileFieldInputPlain}
                    />
                  </Form.Item>
                </Space.Compact>
              </div>
              <Button
                type="primary"
                className={authStyles.verifyRowVerifyBtn}
                onClick={() => handleEmailVerify('Edit')}
                disabled={
                  emailLoading ||
                  emailResendLoading ||
                  !(email || '').trim() ||
                  !isValidEmail((email || '').trim())
                }
                loading={emailLoading}
              >
                {emailLoading
                  ? 'Verifying...'
                  : isEmailVerified
                    ? 'Edit'
                    : 'Verify'}
              </Button>
            </div>

            {showEmailCodeInput && (
              <div className={authStyles.inlineVerificationBlock}>
                <div className={authStyles.inlineVerificationOtp}>
                  <span className={authStyles.inlineVerificationLabel}>Email code</span>
                  <Input.OTP
                    length={6}
                    value={emailOtp}
                    onChange={setEmailOtp}
                    disabled={emailResendLoading}
                  />
                </div>
                <Button
                  type="default"
                  size="small"
                  className={authStyles.inlineVerificationResend}
                  onClick={() => handleEmailVerify('Resend')}
                  disabled={emailResend > 0 || emailResendLoading}
                  loading={emailResendLoading}
                >
                  {emailResend > 0 ? `Resend in ${emailResend}s` : 'Resend code'}
                </Button>
              </div>
            )}
          </div>
        </Form.Item>

        <Form.Item
          label="User Name (Auto-assigned)"
          name="userName"
          rules={[{ required: true, message: 'Verify your mobile first' }]}
        >
          <Input disabled />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: 'Please enter a password' },
                { min: 5, message: 'Must be 5 characters or more' },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <div className={authStyles.formActionsFullWidth}>
            <Button
              type="default"
              onClick={() => navigate('/login')}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={finalLoading}
              disabled={!isFormValid()}
            >
              {finalLoading ? 'Creating Account...' : 'Register'}
            </Button>
          </div>
        </Form.Item>
      </Form>
      </div>
    </div>
    </div>
  );
};

export default Register;
